import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../lib/api';
import { Plus, Trash2 } from 'lucide-react';
import { CountryAutocomplete } from './CountryAutocomplete';

const DAY_PILLS = [
  { label: 'Sen', bit: 1 },
  { label: 'Sel', bit: 2 },
  { label: 'Rab', bit: 4 },
  { label: 'Kam', bit: 8 },
  { label: 'Jum', bit: 16 },
  { label: 'Sab', bit: 32 },
  { label: 'Min', bit: 64 },
];

interface ScheduleConfig {
  tz: string;
  daysMask: number;
  startHour: number;
  endHour: number;
}

/**
 * First-class panel (Stitch screen 96327f0d): kolom kanan drawer — bukan
 * <details> tersembunyi. Self-contained: seluruh state form hidup di sini
 * sehingga RulesDrawer tinggal memasang <SegmentBuilder campaignId offers
 * existingRules onAdded />.
 */
export function SegmentBuilder({
  campaignId,
  offers,
  existingRules,
  onAdded,
}: {
  campaignId: number;
  offers: any[];
  existingRules: any[];
  onAdded: () => void;
}) {
  const qc = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [replaceMode, setReplaceMode] = useState(true);

  // 01 Target traffic
  const [countries, setCountries] = useState<string[]>([]);
  const [countryInput, setCountryInput] = useState('');
  const [device, setDevice] = useState('*');
  const [os, setOs] = useState('*');
  const [source, setSource] = useState('');

  // 02 Offer split
  const [splits, setSplits] = useState<Array<{ offerId: number; weight: number }>>(() =>
    offers.length >= 2
      ? [
          { offerId: offers[0]!.id, weight: 50 },
          { offerId: offers[1]!.id, weight: 50 },
        ]
      : offers.length === 1
      ? [{ offerId: offers[0]!.id, weight: 100 }]
      : [],
  );

  // 03 Schedule (optional)
  const [useSchedule, setUseSchedule] = useState(false);
  const [schedule, setSchedule] = useState<ScheduleConfig>({
    tz: 'UTC',
    daysMask: 127,
    startHour: 0,
    endHour: 24,
  });

  const totalWeight = splits.reduce((s, r) => s + Math.max(0, r.weight ?? 0), 0) || 1;
  const usedOfferIds = new Set(splits.map((s) => s.offerId));
  const availableOffers = offers.filter((o) => !usedOfferIds.has(o.id));

  function addCountry(input: string) {
    const code = input.trim().toUpperCase();
    if (!code) return;
    const codes = code
      .split(/[\s,]+/)
      .map((c) => c.trim())
      .filter((c) => /^[A-Z]{2}$/.test(c) || c === '*' || c === 'GLOBAL' || c === 'ALL');
    const next = [...countries];
    for (const c of codes) {
      const norm = c === '*' || c === 'GLOBAL' || c === 'ALL' ? '*' : c;
      if (!next.includes(norm)) next.push(norm);
    }
    setCountries(next);
    setCountryInput('');
  }
  function removeCountry(c: string) {
    setCountries(countries.filter((x) => x !== c));
  }

  function setSplit(idx: number, patch: Partial<{ offerId: number; weight: number }>) {
    setSplits(splits.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  }
  function redistributeEvenly(list: Array<{ offerId: number; weight: number }>) {
    if (list.length === 0) return list;
    const each = Math.floor(100 / list.length);
    const remainder = 100 - each * list.length;
    return list.map((s, i) => ({ ...s, weight: each + (i === 0 ? remainder : 0) }));
  }
  function removeSplit(idx: number) {
    setSplits(redistributeEvenly(splits.filter((_, i) => i !== idx)));
  }
  function addSplit() {
    if (availableOffers.length === 0) return;
    setSplits(redistributeEvenly([...splits, { offerId: availableOffers[0]!.id, weight: 0 }]));
  }

  const targetCountriesForClash = countries.length === 0 ? ['*'] : countries;
  const clashingRules = existingRules.filter((r: any) => {
    const ruleCountry = (r.country ?? '*').toString().toUpperCase();
    const ruleDevice = (r.deviceType ?? '*').toString();
    const targetMatchCountry = targetCountriesForClash
      .map((c) => c.toUpperCase())
      .includes(ruleCountry);
    const targetMatchDevice = ruleDevice === device;
    const ruleOsWild = !r.os || r.os === '*';
    const ruleSourceWild = !r.source || r.source === '*';
    return targetMatchCountry && targetMatchDevice && ruleOsWild && ruleSourceWild;
  });

  const targetCountries = countries.length === 0 ? ['*'] : countries;
  const totalRulesToCreate = targetCountries.length * splits.length;
  const sumWeights = splits.reduce((s, r) => s + (r.weight ?? 0), 0);
  const isValid = splits.length > 0 && splits.every((s) => s.offerId > 0 && s.weight > 0);
  const schedulePayload =
    useSchedule && schedule.daysMask > 0 && schedule.endHour > schedule.startHour
      ? schedule
      : undefined;

  const conditionSummary =
    [
      targetCountries.includes('*') ? 'any country' : targetCountries.join(', '),
      device !== '*' ? device : null,
      os !== '*' ? os : null,
      source.trim() ? `src=${source.trim()}` : null,
    ]
      .filter(Boolean)
      .join(' × ') || 'any traffic';
  const splitSummary = splits
    .map((s) => `${offers.find((o) => o.id === s.offerId)?.name ?? `#${s.offerId}`}@${s.weight}%`)
    .join(' + ');

  async function submit() {
    if (!isValid) {
      setError('Setiap split harus punya offer dan weight > 0');
      return;
    }
    if (replaceMode && clashingRules.length > 0) {
      const ok = window.confirm(
        `Replace Mode: ${clashingRules.length} existing rule(s) for ${targetCountries.join(', ')} ` +
          `(device=${device === '*' ? 'any' : device}) will be deleted before new ones are created. Continue?`,
      );
      if (!ok) return;
    }
    setError(null);
    setSubmitting(true);
    try {
      if (replaceMode && clashingRules.length > 0) {
        const deleteResults = await Promise.allSettled(
          clashingRules.map((r: any) =>
            apiClient.delete(`/api/v1/campaigns/${campaignId}/rules/${r.id}`),
          ),
        );
        const realFailures = deleteResults
          .map((res, i) => ({ res, rule: clashingRules[i] }))
          .filter(({ res }) => {
            if (res.status === 'fulfilled') return false;
            const msg = (res.reason as Error)?.message ?? '';
            return !msg.includes('404') && !msg.toLowerCase().includes('not found');
          });
        if (realFailures.length > 0) {
          throw new Error(
            `Failed to delete ${realFailures.length} clashing rule(s) — aborted to avoid creating duplicates. ` +
              `First error: ${(realFailures[0]!.res as PromiseRejectedResult).reason?.message ?? 'unknown'}`,
          );
        }
      }
      const createPairs: Array<{ country: string; sp: { offerId: number; weight: number } }> = [];
      for (const country of targetCountries) {
        for (const sp of splits) createPairs.push({ country, sp });
      }
      const createResults = await Promise.allSettled(
        createPairs.map(({ country, sp }) =>
          apiClient.post(`/api/v1/campaigns/${campaignId}/rules`, {
            offerId: sp.offerId,
            country,
            deviceType: device,
            os,
            source: source.trim() || '*',
            weight: sp.weight,
            priority: 100,
            ...(schedulePayload ? { schedule: schedulePayload } : {}),
          }),
        ),
      );
      const createFailures = createResults.filter((r) => r.status === 'rejected');
      if (createFailures.length > 0) {
        throw new Error(
          `Created ${createResults.length - createFailures.length} of ${createResults.length} rules — ${createFailures.length} failed. ` +
            `First error: ${((createFailures[0] as PromiseRejectedResult).reason as Error)?.message ?? 'unknown'}`,
        );
      }
      setCountries([]);
      setCountryInput('');
      setSource('');
      onAdded();
    } catch (e) {
      setError((e as Error).message ?? 'Failed to create rules');
    } finally {
      setSubmitting(false);
    }
  }

  const stepLabel = (num: string, text: string) => (
    <div className="flex items-center gap-2 mb-2">
      <span className="text-[10px] font-mono font-bold text-neon-magenta border border-neon-magenta/40 px-1.5 py-0.5 rounded-sm">
        {num}
      </span>
      <span className="text-[10px] font-display uppercase tracking-wider text-text-secondary">
        {text}
      </span>
    </div>
  );

  return (
    <div className="cyber-panel p-4 relative">
      {/* HUD corner bracket decoration */}
      <span className="absolute top-1 right-1 w-3 h-3 border-t border-r border-neon-cyan/30 pointer-events-none" />
      <div className="text-[10px] font-display uppercase tracking-widest text-neon-magenta mb-1">
        Add Routing Segment
      </div>
      <p className="text-[10px] font-mono text-text-tertiary mb-2 leading-relaxed">
        Submit sekali bikin{' '}
        <span className="text-neon-cyan">{totalRulesToCreate}</span> rule (
        {targetCountries.length} country × {splits.length} offer).
      </p>

      {/* 01 TARGET TRAFFIC */}
      <div className="mb-2">
        {stepLabel('01', 'Target traffic')}
        <div className="flex flex-wrap items-center gap-1.5 px-2 py-2 bg-bg-tertiary border border-border-subtle rounded-sm min-h-[42px]">
          {countries.length === 0 && countryInput.trim() === '' && (
            <span className="text-[10px] font-mono text-text-tertiary italic px-1">
              empty = catch-all · ketik kode 2 huruf (ID, MY, SG) lalu Enter
            </span>
          )}
          {countries.map((c) => (
            <span
              key={c}
              className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-mono rounded border ${
                c === '*'
                  ? 'border-text-tertiary text-text-tertiary bg-bg-secondary'
                  : 'border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5'
              }`}
            >
              {c === '*' ? 'any country' : c}
              <button
                onClick={() => removeCountry(c)}
                className="text-neon-red hover:opacity-70 ml-1"
                title="Remove"
              >
                ×
              </button>
            </span>
          ))}
          <CountryAutocomplete
            value={countryInput}
            onChange={setCountryInput}
            onSelect={(c) => addCountry(c)}
            onBackspaceEmpty={() => {
              if (countries.length > 0) removeCountry(countries[countries.length - 1]!);
            }}
            placeholder={countries.length === 0 ? 'ID, MY, SG, ...' : '+'}
          />
        </div>
        <div className="flex flex-wrap gap-1.5 mt-1.5">
          <span className="text-[10px] font-mono text-text-tertiary">Preset:</span>
          {[
            { label: 'Catch-all', codes: [] as string[] },
            { label: 'SEA T1', codes: ['ID', 'MY', 'SG', 'TH', 'PH'] },
            { label: 'T1 EN', codes: ['US', 'GB', 'CA', 'AU'] },
            { label: 'EU', codes: ['DE', 'FR', 'IT', 'ES', 'NL'] },
          ].map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => setCountries(preset.codes)}
              className="text-[10px] font-mono text-text-secondary hover:text-neon-cyan border border-border-subtle hover:border-neon-cyan/40 px-1.5 py-0.5 rounded"
            >
              {preset.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-3 gap-2 mt-2">
          <select value={device} onChange={(e) => setDevice(e.target.value)} className="input-cyber">
            <option value="*">Any device</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
            <option value="desktop">Desktop</option>
          </select>
          <select value={os} onChange={(e) => setOs(e.target.value)} className="input-cyber">
            <option value="*">Any OS</option>
            <option value="android">Android</option>
            <option value="ios">iOS</option>
            <option value="windows">Windows</option>
            <option value="macos">macOS</option>
          </select>
          <input
            value={source}
            onChange={(e) => setSource(e.target.value)}
            placeholder="source (opt.)"
            list="rj-source-options"
            title="Source dideteksi otomatis dari UA/referrer (Facebook, Instagram, TikTok, LINE, WhatsApp, Telegram, Twitter, Google, YouTube, Snapchat, atau nama browser seperti Chrome). Kosongkan = semua source."
            className="input-cyber"
          />
          <datalist id="rj-source-options">
            {['Facebook', 'Instagram', 'TikTok', 'LINE', 'WhatsApp', 'Telegram', 'Twitter', 'Google', 'YouTube', 'Snapchat'].map((s) => (
              <option key={s} value={s} />
            ))}
          </datalist>
        </div>
      </div>

      {/* 02 OFFER SPLIT */}
      <div className="mb-2">
        <div className="flex items-center justify-between mb-2">
          {stepLabel('02', 'Offer split')}
          <button
            type="button"
            onClick={() => setSplits(redistributeEvenly(splits))}
            className="text-[9px] font-mono text-neon-cyan hover:underline"
            title="Auto-set weights to sum 100"
          >
            even split
          </button>
        </div>
        <div className="space-y-1.5">
          {splits.map((s, i) => {
            const pct = (Math.max(0, s.weight ?? 0) / totalWeight) * 100;
            return (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={s.offerId}
                  onChange={(e) => setSplit(i, { offerId: Number(e.target.value) })}
                  className="input-cyber flex-1 min-w-0"
                >
                  {offers.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name}
                    </option>
                  ))}
                </select>
                <div className="relative w-20 shrink-0">
                  <input
                    type="number"
                    value={s.weight}
                    onChange={(e) => setSplit(i, { weight: Number(e.target.value) })}
                    className="input-cyber pr-6 text-right"
                    min={0}
                    max={100}
                  />
                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-text-tertiary pointer-events-none">
                    %
                  </span>
                </div>
                <div className="flex-1 h-2 bg-bg-tertiary rounded overflow-hidden min-w-[48px]">
                  <div className="h-full bg-neon-cyan transition-all" style={{ width: `${pct}%` }} />
                </div>
                <button
                  type="button"
                  onClick={() => removeSplit(i)}
                  className="text-neon-red hover:opacity-70 disabled:opacity-30 shrink-0"
                  disabled={splits.length <= 1}
                  title="Remove"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            );
          })}
          {availableOffers.length > 0 && (
            <button
              type="button"
              onClick={addSplit}
              className="inline-flex items-center gap-1 text-[10px] font-mono text-neon-magenta hover:underline pt-1"
            >
              <Plus size={10} /> add offer to split
            </button>
          )}
        </div>
        <div
          className={`inline-flex items-center gap-1 mt-2 px-1.5 py-0.5 rounded border text-[10px] font-mono ${
            sumWeights === 100
              ? 'text-neon-green border-neon-green/40 bg-neon-green/5'
              : sumWeights > 0
              ? 'text-neon-yellow border-neon-yellow/40 bg-neon-yellow/5'
              : 'text-text-tertiary border-border-subtle'
          }`}
        >
          Σ {sumWeights}%
          {sumWeights === 100
            ? ' · exact'
            : sumWeights > 0
            ? ` · dinormalisasi ke 100%`
            : ' · set weights'}
        </div>
      </div>

      {/* 03 SCHEDULE (OPTIONAL) */}
      <div className="mb-2">
        {stepLabel('03', 'Schedule')}
        <label className="flex items-center gap-2 cursor-pointer mb-2">
          <input
            type="checkbox"
            checked={useSchedule}
            onChange={(e) => setUseSchedule(e.target.checked)}
            className="accent-neon-magenta"
          />
          <span className="text-[10px] font-mono text-text-secondary">
            batasi jam tayang rule ini
          </span>
        </label>
        {useSchedule && (
          <div className="space-y-2 pl-1">
            <div className="flex gap-2">
              <select
                value={schedule.tz}
                onChange={(e) => setSchedule({ ...schedule, tz: e.target.value })}
                className="input-cyber flex-1"
              >
                <option value="UTC">UTC</option>
                <option value="Asia/Jakarta">Asia/Jakarta (WIB)</option>
              </select>
              <select
                value={schedule.startHour}
                onChange={(e) => setSchedule({ ...schedule, startHour: Number(e.target.value) })}
                className="input-cyber w-20"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h} value={h}>
                    {String(h).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
              <select
                value={schedule.endHour}
                onChange={(e) => setSchedule({ ...schedule, endHour: Number(e.target.value) })}
                className="input-cyber w-20"
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <option key={h + 1} value={h + 1}>
                    {String(h + 1).padStart(2, '0')}:00
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-1">
              {DAY_PILLS.map((d) => {
                const active = (schedule.daysMask & d.bit) !== 0;
                return (
                  <button
                    key={d.bit}
                    type="button"
                    onClick={() =>
                      setSchedule({
                        ...schedule,
                        daysMask: active ? schedule.daysMask & ~d.bit : schedule.daysMask | d.bit,
                      })
                    }
                    className={`w-7 py-0.5 text-[10px] font-mono rounded border transition-colors ${
                      active
                        ? 'text-neon-cyan border-neon-cyan/50 bg-neon-cyan/10'
                        : 'text-text-tertiary border-border-subtle'
                    }`}
                  >
                    {d.label}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Replace Mode */}
      <div className="flex items-start gap-2 pt-3 border-t border-border-subtle">
        <input
          type="checkbox"
          id={`replace-mode-${campaignId}`}
          checked={replaceMode}
          onChange={(e) => setReplaceMode(e.target.checked)}
          className="mt-0.5 accent-neon-magenta"
        />
        <label htmlFor={`replace-mode-${campaignId}`} className="flex-1 cursor-pointer">
          <span className="text-[11px] font-display uppercase tracking-wider text-neon-magenta">
            Replace mode
          </span>
          <p className="text-[10px] font-mono text-text-tertiary leading-relaxed mt-0.5">
            {replaceMode ? (
              <>
                Hapus rule lama dengan combo (country × device) sama sebelum membuat yang baru.{' '}
                {clashingRules.length > 0 ? (
                  <span className="text-neon-yellow">
                    {clashingRules.length} rule akan diganti.
                  </span>
                ) : (
                  <span className="text-neon-green">Tanpa clash — clean add.</span>
                )}
              </>
            ) : (
              <>Off → rule ditambah di atas existing (bisa duplikasi weight bertumpuk).</>
            )}
          </p>
        </label>
      </div>

      {/* Summary strip + deploy */}
      <div className="pt-3 mt-3 border-t border-border-subtle space-y-2">
        <div className="px-2 py-1.5 bg-bg-primary border border-border-subtle rounded-sm text-[10px] font-mono text-text-secondary break-words">
          <span className="text-neon-cyan">creates {totalRulesToCreate}:</span>{' '}
          {conditionSummary} → {splitSummary}
          {schedulePayload && (
            <>
              {' '}
              · <span className="text-neon-yellow">
                {schedule.tz} {String(schedule.startHour).padStart(2, '0')}–
                {String(schedule.endHour).padStart(2, '0')}
              </span>
            </>
          )}
        </div>
        <button
          type="button"
          onClick={submit}
          disabled={!isValid || offers.length === 0 || splits.length === 0 || submitting}
          className="w-full py-2 font-display uppercase tracking-widest text-xs rounded-sm bg-neon-magenta/90 text-bg-primary font-bold hover:bg-neon-magenta disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-[0_0_12px_rgba(255,43,214,0.35)]"
        >
          {submitting
            ? 'Deploying...'
            : replaceMode && clashingRules.length > 0
            ? `Replace ${clashingRules.length} → Deploy ${totalRulesToCreate}`
            : `Deploy ${totalRulesToCreate} Rule${totalRulesToCreate !== 1 ? 's' : ''}`}
        </button>
        {error && <p className="text-[10px] font-mono text-neon-red">{error}</p>}
      </div>
    </div>
  );
}
